import { useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/ui/sheet';
import { NavBadge } from './NavBadge';
import type { TopNavActionId, TopNavBadgeDef, TopNavItemDef } from './navItems';

/** Runtime state of a top-navigation action, supplied by the shell (never fetched here). */
export interface TopNavActionState {
  /** Whether the entry is rendered at all; when false it is absent, not disabled. */
  readonly visible: boolean;
  /** An activation is in flight; the button is disabled and `aria-busy`. */
  readonly pending: boolean;
  readonly onActivate: () => void;
}

interface AppTopNavProps {
  /** The nav-item set to render, e.g. {@link import('./navItems').CUSTOMER_TOP_NAV_ITEMS}. */
  readonly items: readonly TopNavItemDef[];
  /**
   * Counts for the dynamic Top-Nav badges, keyed by each item's {@link TopNavBadgeDef.source}. A
   * missing or `undefined` entry renders no badge for that item — `AppTopNav` never fetches this
   * data itself.
   */
  readonly badges?: Record<string, number | undefined>;
  /** State per action id; an action item without a visible state is not rendered. */
  readonly actions?: Partial<Record<TopNavActionId, TopNavActionState>>;
}

interface TopNavLinkProps {
  readonly to: string;
  readonly icon: ReactNode;
  readonly children: ReactNode;
  readonly onNavigate?: () => void;
}

/**
 * A single nav entry: icon + label, underlined in the active state.
 *
 * Renders `NavLink`, whose active state is path-based: a query parameter such as `fachbereich`
 * (from the dashboard's "zur Übersicht" tiles or the Auftragsliste's `FachbereichFilter` pills)
 * or `page`/`sort` never affects it, and a sub-route (e.g. `/logistik/lieferdienstverwaltung`)
 * keeps its parent item active. Only the root item (`/`, "Übersicht") is matched with `end` —
 * otherwise it would be active on every route. `NavLink` computes `aria-current="page"` itself;
 * no bespoke active-state logic is needed.
 */
function TopNavLink({ to, icon, children, onNavigate }: TopNavLinkProps) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 whitespace-nowrap border-b-2 border-transparent px-4 py-3.5 text-sm font-medium text-foreground transition-colors hover:text-primary',
          isActive && 'border-primary text-primary',
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}

interface TopNavActionButtonProps {
  readonly state: TopNavActionState;
  readonly icon: ReactNode;
  readonly children: ReactNode;
  readonly onNavigate?: () => void;
}

/**
 * A nav entry that triggers an action instead of navigating. Opens an external target in a new
 * tab, which is announced to screenreaders via a visually hidden suffix.
 */
function TopNavActionButton({ state, icon, children, onNavigate }: TopNavActionButtonProps) {
  const { t } = useTranslation('app');
  return (
    <button
      type="button"
      onClick={() => {
        state.onActivate();
        onNavigate?.();
      }}
      aria-busy={state.pending}
      disabled={state.pending}
      className="flex items-center gap-2 whitespace-nowrap border-b-2 border-transparent px-4 py-3.5 text-sm font-medium text-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
    >
      {icon}
      {children}
      <span className="sr-only"> {t('topNav.opensInNewTab')}</span>
    </button>
  );
}

/**
 * Renders an item's counter badge, wrapped in the `aria-live="polite"` region that announces a
 * changing count. Returns `null` for an item without a badge, or when no count has resolved —
 * `NavBadge` itself drops a count below 1, so a loading/errored query shows nothing rather than a
 * stale value. Both the desktop bar and the mobile drawer go through here so the two renderings
 * cannot drift apart.
 */
function renderBadge(
  badge: TopNavBadgeDef | undefined,
  badges: AppTopNavProps['badges'],
): ReactNode {
  if (badge === undefined) return null;

  // Conditional spread, not `max={badge.max}`: `exactOptionalPropertyTypes` rejects an explicit
  // `undefined` for the optional prop, whose own default must apply instead.
  const maxProp = badge.max !== undefined ? { max: badge.max } : {};

  return (
    <span aria-live="polite">
      <NavBadge
        count={badges?.[badge.source] ?? 0}
        labelKey={badge.labelKey}
        cappedLabelKey={badge.cappedLabelKey}
        {...maxProp}
      />
    </span>
  );
}

/**
 * "Header Nav Row" of the portal shell chrome (Pencil node `TI0LX`): a presentational component
 * driven entirely by the `items` prop (e.g. `CUSTOMER_TOP_NAV_ITEMS` from `navItems.ts`). The
 * `border-t` divider against the Top Row above sits on the full-width outer element, while the
 * nav items themselves share the shell-wide `max-w-[1440px]` centered content column. Renders as
 * a horizontal bar from `md` upward and collapses into a `Sheet` drawer, triggered by an
 * icon-only menu button, below `md`.
 */
export function AppTopNav({ items, badges, actions }: AppTopNavProps) {
  const { t } = useTranslation('app');
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobileNav = (): void => setMobileOpen(false);

  const renderItem = (item: TopNavItemDef, onNavigate?: () => void): ReactNode => {
    const icon = <item.icon className="h-4 w-4" aria-hidden="true" />;
    if (item.kind === 'action') {
      const state = actions?.[item.action];
      if (state?.visible !== true) return null;
      return (
        <TopNavActionButton
          key={item.labelKey}
          state={state}
          icon={icon}
          {...(onNavigate !== undefined ? { onNavigate } : {})}
        >
          {t(item.labelKey)}
        </TopNavActionButton>
      );
    }
    return (
      <TopNavLink
        key={item.labelKey}
        to={item.to}
        icon={icon}
        {...(onNavigate !== undefined ? { onNavigate } : {})}
      >
        {t(item.labelKey)}
        {renderBadge(item.badge, badges)}
      </TopNavLink>
    );
  };

  return (
    <div className="border-t">
      <nav
        aria-label={t('topNav.ariaLabel')}
        className="mx-auto hidden max-w-[1440px] items-center gap-1 overflow-x-auto px-6 md:flex"
      >
        {items.map((item) => renderItem(item))}
      </nav>

      <div className="mx-auto flex max-w-[1440px] items-center px-4 py-1.5 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              aria-label={t('topNav.openMenu')}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72">
            <SheetHeader>
              <SheetTitle>{t('topNav.ariaLabel')}</SheetTitle>
              <SheetDescription className="sr-only">{t('topNav.ariaLabel')}</SheetDescription>
            </SheetHeader>
            <nav aria-label={t('topNav.ariaLabel')} className="mt-4 flex flex-col gap-1">
              {items.map((item) => renderItem(item, closeMobileNav))}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
